import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ThemeProvider } from '@material-ui/styles';
import createAppTheme from './index';
import { applyChartTheme } from './chartTheme';
import { cssVariablesFor, ThemeMode } from './tokens';

const STORAGE_KEY = 'elevate-theme-mode';
const STYLE_ELEMENT_ID = 'ev-theme-tokens';

interface IThemeModeContext {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<IThemeModeContext>({
  mode: 'light',
  toggleMode: () => undefined,
  setMode: () => undefined,
});

export const useThemeMode = () => useContext(ThemeModeContext);

const DARK_QUERY = '(prefers-color-scheme: dark)';

/** The user's explicit choice (set via the toggle), or null if they never made one. */
const readStoredMode = (): ThemeMode | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'dark' || stored === 'light' ? stored : null;
  } catch {
    return null;
  }
};

/** The operating system's current light/dark preference. */
const systemMode = (): ThemeMode =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia(DARK_QUERY).matches
    ? 'dark'
    : 'light';

/** Publishes both token sets as CSS variables; `data-theme` picks the set. */
const injectTokens = () => {
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ELEMENT_ID;
  el.textContent =
    `:root{${cssVariablesFor('light')}color-scheme:light;}` +
    `:root[data-theme="dark"]{${cssVariablesFor('dark')}color-scheme:dark;}`;
  document.head.appendChild(el);
};

injectTokens();

export const ThemeModeProvider: React.FC = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(
    () => readStoredMode() ?? systemMode(),
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    applyChartTheme(mode);
  }, [mode]);

  // Until the user picks a mode themselves, follow the OS setting live.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = () => {
      if (readStoredMode() === null) setModeState(systemMode());
    };
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);

  /** An explicit choice: apply it and remember it, overriding the OS setting. */
  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable (private mode) — the choice just won't persist */
    }
  }, []);
  const toggleMode = useCallback(
    () => setMode(mode === 'dark' ? 'light' : 'dark'),
    [mode, setMode],
  );

  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const value = useMemo(
    () => ({ mode, toggleMode, setMode }),
    [mode, toggleMode, setMode],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
